import cuid from "cuid";
import { filter, get, map } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { CREATE_ATTACHMENT } from "utils/api/graphql/mutations/attachment";
import { ATTACHMENT_TYPES, KEYS, RELATIONSHIP } from "utils/constants";
import Template from "../Template";
import View from "./View";

export const DOCUMENTS_TYPE = {
  healthInsuranceRightsCertificate:
    ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
  bankAccountDetailsForPayment:
    ATTACHMENT_TYPES.BANK_ACCOUNT_DETAILS_FOR_PAYMENT,
  childDependentProof: ATTACHMENT_TYPES.CHILD_DEPENDENT_PROOF,
};

const SpouseJustification = ({
  currentStatus,
  tracer,
  project,
  onBack,
  updateUrl,
  onNext,
}) => {
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("B2C_LMF.SPOUSE_JUSTIFICATION", {
    returnObjects: true,
  });
  const subscriptionId =
    get(project, "fields.currentSubscription") ||
    localStorage.getItem(KEYS.SUBSCRIPTION_ID);

  const { data: subscription } = useQuery(graphql.queries.SUBSCRIPTION, {
    variables: { where: { id: subscriptionId } },
  });

  const spouse = get(project, "contact.relatedPersons", []).find(
    ({ relationship }) => relationship === RELATIONSHIP.SPOUSE,
  );
  const spouseUserId = get(spouse, "identity.user.id");

  const { loading: attachmentLoading } = useQuery(graphql.queries.ATTACHMENTS, {
    fetchPolicy: "no-cache",
    variables: {
      where: {
        AND: [
          { subscription: { id: subscriptionId } },
          { user: { id: spouseUserId } },
        ],
      },
      isIn: {
        type: Object.values(DOCUMENTS_TYPE),
      },
    },

    onCompleted: ({ attachments: { data: attachmentsList } }) => {
      setInitialValues({
        healthInsuranceRightsCertificate: map(
          filter(
            attachmentsList,
            ({ type }) =>
              type === ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
          ),
          ({ id, name, fileUrl }) => ({
            uid: id,
            name,
            status: "done",
            url: fileUrl,
          }),
        ),
        bankAccountDetailsForPayment: map(
          filter(
            attachmentsList,
            ({ type }) =>
              type === ATTACHMENT_TYPES.BANK_ACCOUNT_DETAILS_FOR_PAYMENT,
          ),
          ({ id, name, fileUrl }) => ({
            uid: id,
            name,
            status: "done",
            url: fileUrl,
          }),
        ),
        childDependentProof: map(
          filter(
            attachmentsList,
            ({ type }) => type === ATTACHMENT_TYPES.CHILD_DEPENDENT_PROOF,
          ),
          ({ id, name, fileUrl }) => ({
            uid: id,
            name,
            status: "done",
            url: fileUrl,
          }),
        ),
        // includedChildren: get(project, "fields.includedChildren"),
      });
      setLoading(false);
    },
  });
  const [addAttachment] = useMutation(CREATE_ATTACHMENT);

  const onSubmit = async ({ includedChildren, ...documents }) => {
    setLoading(true);
    const documentsToUpload = [];
    if (!spouseUserId) {
      return;
    }

    Object.entries(documents)?.map(([key, attachments]) =>
      attachments
        ?.filter((e) => !e.status)
        .forEach(({ name, contentType, base64 }) => {
          documentsToUpload.push({
            id: cuid(),
            subscription: { id: subscriptionId },
            user: { id: spouseUserId },
            name,
            content: base64,
            contentType,
            type: DOCUMENTS_TYPE[key],
          });
        }),
    );
    await Promise.all(
      documentsToUpload.map((document, index) =>
        addAttachment({
          variables: {
            data: document,
          },
        }),
      ),
    );

    const source = localStorage.getItem(KEYS.SOURCE);
    updateUrl({
      subscriptionId,
      url: get(
        subscription,
        "subscription.insurancePlan.config.generatedUrl",
        `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
      ),
    });
    onNext({
      payload: {
        fields: {
          ...get(project, "fields"),
          includedChildren,
        },
      },
    });

    // signDocuments({
    //   variables: {
    //     data: { id: subscriptionId },
    //   },
    //   onCompleted: () => {
    //     const source = localStorage.getItem(KEYS.SOURCE);
    //     notification.open({
    //       message: TRANSLATION.E_SIGNATURE_SENT,
    //       duration: 5,
    //       icon: <IoCheckmarkCircleOutline color={COLORS.C_SUCCESS} />,
    //     });
    //     updateUrl({
    //       subscriptionId,
    //       url: get(
    //         subscription,
    //         "subscription.insurancePlan.config.generatedUrl",
    //         `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`
    //       ),
    //     });
    //     onNext({
    //       payload: {
    //         fields: {
    //           ...get(project, "fields"),
    //           includedChildren,
    //         },
    //       },
    //     });
    //   },
    // });
  };

  return (
    <Template
      progress={currentStatus.progress}
      onBack={onBack}
      loading={loading || attachmentLoading || !initialValues}
      project={project}
      View={
        initialValues && (
          <View
            key={JSON.stringify(initialValues)}
            locked={get(subscription, "subscription.locked", false)}
            onSubmit={onSubmit}
            initialValues={initialValues}
            translate={TRANSLATION}
            onBack={onBack}
          />
        )
      }
      HeaderComponent={{
        subscription: get(subscription, "subscription"),
        tracer,
        project,
        contact: get(project, "contact"),
      }}
      contact={get(project, "contact")}
    />
  );
};
export default SpouseJustification;
